// Helper functions to manage randomization statistics
import { fetchStats, incrementStat } from './statsApi';

// Initialize stats if they don't exist locally
const initializeStats = async () => {
  try {
    // Try to get stats from the server first
    const serverStats = await fetchStats();
    
    // Map server stats to local format
    const stats = {
      godsRolled: serverStats.godsRolled || 0,
      buildsRolled: serverStats.buildsCreated || 0, 
      totalRolls: serverStats.totalRandomizations || 0
    };
    
    // Store in localStorage as cache
    localStorage.setItem('smiteRandomizerStats', JSON.stringify(stats));
    return stats;
  } catch (error) {
    console.error('Failed to fetch stats from server, using local data', error);
    
    // Fall back to localStorage if server request fails
    if (!localStorage.getItem('smiteRandomizerStats')) {
      const initialStats = {
        godsRolled: 0,
        buildsRolled: 0, 
        totalRolls: 0
      };
      localStorage.setItem('smiteRandomizerStats', JSON.stringify(initialStats));
      return initialStats;
    }
    
    try {
      return JSON.parse(localStorage.getItem('smiteRandomizerStats'));
    } catch (e) {
      console.error('Failed to parse stats, resetting');
      const initialStats = {
        godsRolled: 0,
        buildsRolled: 0,
        totalRolls: 0
      };
      localStorage.setItem('smiteRandomizerStats', JSON.stringify(initialStats));
      return initialStats;
    }
  }
};

// Get current stats (with optional immediate local fallback)
const getStats = async (useLocalFallback = true) => {
  try {
    return await initializeStats();
  } catch (error) {
    console.error('Error getting stats:', error);
    // Return local stats immediately if requested
    if (useLocalFallback) {
      try {
        return JSON.parse(localStorage.getItem('smiteRandomizerStats')) || {
          godsRolled: 0,
          buildsRolled: 0,
          totalRolls: 0
        };
      } catch (e) {
        return {
          godsRolled: 0,
          buildsRolled: 0,
          totalRolls: 0
        };
      }
    }
    throw error;
  }
};

// Increment god rolls counter
const incrementGodRolls = async () => {
  try {
    // Update server first
    await incrementStat('godsRolled');
    await incrementStat('totalRandomizations');
    
    // Get updated stats from server
    const stats = await getStats();
    return stats;
  } catch (error) {
    console.error('Failed to update server stats for godsRolled', error);
    
    // Fall back to local updates if server request fails
    const stats = await getStats(true);
    stats.godsRolled += 1;
    stats.totalRolls += 1;
    localStorage.setItem('smiteRandomizerStats', JSON.stringify(stats));
    return stats;
  }
};

// Increment build rolls counter
const incrementBuildRolls = async () => {
  try {
    // Update server first
    await incrementStat('buildsCreated');
    await incrementStat('totalRandomizations');
    
    // Get updated stats from server
    const stats = await getStats();
    return stats;
  } catch (error) {
    console.error('Failed to update server stats for buildsCreated', error);
    
    // Fall back to local updates if server request fails
    const stats = await getStats(true);
    stats.buildsRolled += 1;
    stats.totalRolls += 1;
    localStorage.setItem('smiteRandomizerStats', JSON.stringify(stats));
    return stats;
  }
};

export {
  initializeStats,
  incrementGodRolls,
  incrementBuildRolls,
  getStats
}; 